import type { MouseEventHandler } from 'react';
import { Link } from 'react-router-dom';
import { filterTargetsByPeriod, rankTargetsByPace } from '../../lib/targets';
import { calendarMonthName, previousMonthKey } from '../../../shared/dates';
import type { GoalMetric } from '../../../shared/stats';
import { Group, GroupLink, Pill } from '../../components/primitives';
import { SettingsIcon } from '../../components/icons';
import { useIsMobile } from '../../lib/useIsMobile';
import type { PreviousOverviewGoal, OverviewTarget, OverviewTargetsSettings } from '../../../shared/overview';
import { GoalCopyButton } from '../../components/GoalCopyButton';
import { compactMoney, targetPeriodAbbr } from './helpers';

// ── Targets strip (feature-builds, July 2026) — a compact readout of
//    Stats' goal cards, ranked by how far behind pace they are so the
//    thing most needing a look surfaces without opening Stats. ──────────

const TARGET_METRIC_LABELS: Record<GoalMetric, string> = {
  revenue_ex: 'REVENUE',
  leads: 'LEADS',
  proposals_delivered: 'PROPOSALS & QUOTES',
  jobs_won: 'JOBS WON',
};
// The 92px phone column can't hold the full label (workflow D1, Oct 2026),
// so phones keep the short form — same convention as the Stats goal cards'
// shortTitle. The metric still counts both kinds.
const TARGET_METRIC_LABELS_MOBILE: Partial<Record<GoalMetric, string>> = {
  proposals_delivered: 'PROPOSALS',
};
function carriedDescription(t: OverviewTarget): { sentence: string; period: string } {
  const period = t.period === 'month' ? calendarMonthName(t.period_key) : t.period_key;
  const source = t.source_period_key
    ? t.period === 'month' ? calendarMonthName(t.source_period_key) : t.source_period_key
    : 'the previous period';
  const metric = t.metric === 'proposals_delivered' ? 'proposals' : TARGET_METRIC_LABELS[t.metric].toLowerCase();
  return { period, sentence: `No ${metric} target set for ${period}, so ${source}'s ${t.metric === 'revenue_ex' ? compactMoney(t.target) : t.target} is carried forward.` };
}
export function TargetsBand({ targets, today, settings, onChoose, chooserOpen = false, chooserSaving = false, previous = [], onCopied = () => {} }: {
  targets: OverviewTarget[]; today: string; settings: OverviewTargetsSettings;
  onChoose?: MouseEventHandler<HTMLButtonElement>; chooserOpen?: boolean; chooserSaving?: boolean; previous?: PreviousOverviewGoal[]; onCopied?: () => void;
}) {
  const ranked = rankTargetsByPace(targets, today, settings);
  const tracked = filterTargetsByPeriod(targets, settings).filter(t => settings.targets.includes(t.metric));
  const isMobile = useIsMobile();
  const month = calendarMonthName(today);
  // The copy strip is deliberately not filtered by target_periods: the copy
  // route writes every metric for a whole period, so the text must match.
  const copyable = previous.filter(goal => goal.copyable === true);
  const previousMonth = calendarMonthName(previousMonthKey(today.slice(0, 7)));
  const summary = previous.length ? [...new Set(previous.map(t => t.period_key))].map(key => {
    const goals = previous.filter(t => t.period_key === key);
    return `${key.includes(' Q') ? key : calendarMonthName(key)} had ${goals.length}: ${goals.map(t => `${t.metric === 'proposals_delivered' ? 'proposals' : TARGET_METRIC_LABELS[t.metric].toLowerCase()} ${t.metric === 'revenue_ex' ? compactMoney(t.target) : t.target}`).join(', ')}.`;
  }).join(' ') : `${calendarMonthName(previousMonthKey(today.slice(0, 7)))} had no targets to copy.`;
  return <Group title="Targets" count={`${tracked.length} tracked`} link={<>
    <button type="button" className={`choose-btn${chooserOpen ? ' on' : ''}`} aria-label="Choose targets" aria-expanded={chooserOpen} aria-haspopup="dialog" disabled={chooserSaving} onClick={onChoose}><SettingsIcon />{!isMobile && 'Choose'}</button>
    <GroupLink to="/stats">{isMobile ? 'Stats →' : 'Full breakdown → Stats'}</GroupLink>
  </>}>
    {ranked.length === 0 && <div className="tgt-empty"><span>{settings.targets.length === 0 ? 'No metrics selected.' : targets.length ? 'No selected metrics have targets.' : `No targets for ${month} yet.`}
      <span className="sub">{summary}</span></span>
      <GoalCopyButton className="btn primary" label={copyable.length ? "Copy last month's targets" : `Nothing to copy from ${previousMonth}`} disabled={copyable.length === 0}
        periods={[...new Set(copyable.map(t => t.period))]} onCopied={onCopied} />
      <GroupLink to="/stats">Set on Stats →</GroupLink>
    </div>}
    <div className="tgt-strip">{ranked.map(t => {
      const description = carriedDescription(t);
      return <div className="tgt-row" key={`${t.metric}-${t.period_key}`}>
        <span className="tgt-label">{(isMobile && TARGET_METRIC_LABELS_MOBILE[t.metric]) || TARGET_METRIC_LABELS[t.metric]} · {isMobile && t.period === 'month' ? 'M' : targetPeriodAbbr(t.period, t.period_key)}</span>
        <div className="hbar-track"><span className={`hbar-fill ${t.metric === 'revenue_ex' ? 'gold' : 'accent'}`} style={{ width: `${Math.max(0, Math.min(100, t.pct))}%` }} /></div>
        <span className="tgt-value">{t.metric === 'revenue_ex' ? compactMoney(t.actual) : t.actual}{' '}
          <span className="dim">/ {t.metric === 'revenue_ex' ? compactMoney(t.target) : t.target}{t.carried_forward ? '*' : ''}</span></span>
        <span className="tgt-pct">{t.pct}%</span><Pill tone={t.onPace ? 'ok' : 'gold'}>{t.onPace ? 'on pace' : 'behind'}</Pill>
        {t.carried_forward && <span className="pill cf" title={`${description.sentence} Set ${description.period}'s →`}>carried forward</span>}
      </div>;
    })}</div>
    {ranked.filter(t => t.carried_forward).map(t => {
      const description = carriedDescription(t);
      return <div className="tgt-foot" key={`${t.metric}-${t.period_key}`}><span className="mark">*</span><span>
        {isMobile && <><span className="pill cf">carried forward</span>{' '}</>}{description.sentence}{' '}
        <Link to="/stats">Set {description.period}'s →</Link></span></div>;
    })}
  </Group>;
}
